import React, { useEffect, useMemo, useState } from "react";
import { Alert, Box, Button, Snackbar, Typography } from "@mui/material";
import AssessmentIcon from "@mui/icons-material/Assessment";
import { GridLoader } from "react-spinners";
import { useNavigate } from "react-router-dom";
import {
  buscarSumarizacaoIcmsApuracao,
  exportarSumarizacaoApuracao,
} from "../../../services/apuracao.service";
import Pagination from "../../../components/Pagination";
import BotaoExportacaoProgresso from "../../../components/BotaoExportacaoProgresso";
import useExportacaoAssincrona from "../Detalhe/hooks/useExportacaoAssincrona";
import ApuracaoCardShell from "../components/ApuracaoCardShell";
import ModalReprocessarAba from "../components/ModalReprocessarAba";
import SelectFiltro, { BuscaFiltro } from "../components/SelectFiltro";
import { fmtMesAno, formatarCnpj, sumarizacaoEmAndamento } from "../utils";
import { formatarMoedaBRL } from "../Detalhe/utils/calcularTotaisPorAno.util";
import styles from "../Detalhe/detalhe.styles.module.css";
import tableStyles from "../../../components/TableCard/TableView/styles.module.css";
import {
  usePermissoes,
  CHAVES_APURACOES_EXPORT,
} from "../../../config/Context/PermissoesContext";

const FALTA_E111_OPCOES = [
  { value: "", label: "Todas" },
  { value: "sim", label: "Com falta" },
  { value: "nao", label: "Sem falta" },
];

const NUM_TH = { textAlign: "right" };
const NUM_TD = { textAlign: "right", fontFamily: "ui-monospace, monospace" };

function competenciaYm(competencia) {
  return String(competencia || "").slice(0, 7);
}

/**
 * TXM-472 — heurística do card: SIM quando algum ajuste/estorno/dedução/débito especial do E110
 * está informado (indício de que deveria existir E111). Reforçada com dado real que já temos
 * (qtdAjustesE111, join contra sped_registros_fiscal registro='E111' — ver
 * icmsapuracao_preencher.sql): só marca falta quando a heurística indica ajuste E não há nenhum
 * E111 de fato — isso já cobre parte do objetivo de TXM-476 (substituir heurística pura por
 * verificação real), sem esperar a tela de lançamentos dedicada.
 */
function calcularFaltaE111(linha) {
  const indicaAjuste = Boolean(
    Number(linha.vlAjDebitos) ||
      Number(linha.vlEstornosCred) ||
      Number(linha.vlAjCreditos) ||
      Number(linha.vlEstornosDeb) ||
      Number(linha.vlTotDed) ||
      Number(linha.debEsp)
  );
  const temE111 = Number(linha.qtdAjustesE111) > 0;
  return indicaAjuste && !temE111;
}

/**
 * "Débitos do período", usado no KPI "Total débitos apurados" do dashboard.
 *
 * BUG CORRIGIDO (achado 2026-09-14, dado real): o card TXM-472 documenta a fórmula como
 * `vl_tot_aj_debitos + vl_estornos_cred` (a mesma usada aqui de 11/09 a 14/09) — mas essa
 * fórmula NUNCA reconcilia com `vl_sld_apurado` (o saldo que o próprio SPED do cliente
 * informa), testado nas 32 competências reais do CNPJ .154 (2023-2026): diffs de
 * -R$216.952 a -R$528.723 em TODAS elas. Causa: `vl_tot_aj_debitos` (campo 04 do E110) é
 * derivado só dos ajustes E111 (bate exato com `vl_tot_ajustes_e111`), não é
 * "vl_tot_debitos + vl_aj_debitos" como o nome sugere — a fórmula do card ficou faltando
 * o próprio `vl_tot_debitos` (campo 02, o débito das saídas). A fórmula oficial do Guia
 * Prático EFD ICMS/IPI (Débitos = vl_tot_debitos + vl_tot_aj_debitos + vl_estornos_cred;
 * Créditos = vl_tot_creditos + vl_tot_aj_creditos + vl_estornos_deb + vl_sld_credor_ant)
 * bate EXATO com vl_sld_apurado nas 32/32 competências testadas. Card também precisa ser
 * corrigido — a fórmula errada está documentada lá, não só aqui.
 */
function debitosDoPeriodo(linha) {
  return (
    (Number(linha.vlTotDebitos) || 0) +
    (Number(linha.vlTotAjDebitos) || 0) +
    (Number(linha.vlEstornosCred) || 0)
  );
}

function calcularTotais(linhas) {
  const totais = linhas.reduce(
    (acc, linha) => {
      acc.icmsRecolher += Number(linha.vlIcmsRecolher) || 0;
      acc.totalDebitosApurados += debitosDoPeriodo(linha);
      if (calcularFaltaE111(linha)) acc.periodosComFaltaE111 += 1;
      return acc;
    },
    { icmsRecolher: 0, totalDebitosApurados: 0, periodosComFaltaE111: 0 }
  );
  const ultima = [...linhas].sort((a, b) =>
    competenciaYm(a.competencia).localeCompare(competenciaYm(b.competencia))
  )[linhas.length - 1];
  return {
    ...totais,
    // Saldo credor a transportar é um saldo corrido (não um fluxo) — mesmo assim o card TXM-472
    // pede "(soma)" no dashboard; seguindo a especificação literalmente.
    sldCredorTransportar: Number(ultima?.vlSldCredorTransportar) || 0,
  };
}

export default function AbaIcms({ apuracaoId, onTotaisChange, bare }) {
  const navigate = useNavigate();
  const { pode } = usePermissoes();
  const podeExportar = pode(CHAVES_APURACOES_EXPORT);

  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState("");
  const [linhas, setLinhas] = useState([]);
  const [cnpj, setCnpj] = useState("");
  const [empresa, setEmpresa] = useState("");
  const [infoSnack, setInfoSnack] = useState({ open: false, msg: "", severity: "info" });
  const [cnpjFiltro, setCnpjFiltro] = useState("");
  const [buscaFiltro, setBuscaFiltro] = useState("");
  const [competenciaFiltro, setCompetenciaFiltro] = useState("");
  const [faltaE111Filtro, setFaltaE111Filtro] = useState("");
  const [linhasPagina, setLinhasPagina] = useState([]);
  const [modalReprocessarAberto, setModalReprocessarAberto] = useState(false);

  useEffect(() => {
    let ativo = true;
    async function carregar() {
      setLoading(true);
      setErro("");
      const res = await buscarSumarizacaoIcmsApuracao(apuracaoId);
      if (!ativo) return;
      if (!res.sucesso) {
        setErro(res.mensagem || "Sumarização indisponível para esta apuração.");
        setLoading(false);
        return;
      }
      setLinhas(res.linhas);
      setCnpj(res.apuracao?.cnpj || "");
      setEmpresa(res.apuracao?.empresa || "");
      setLoading(false);
    }
    carregar();
    return () => {
      ativo = false;
    };
  }, [apuracaoId]);

  /** TXM-418 — apuração ainda é de 1 CNPJ (multi-CNPJ é o TXM-601); filtro equaliza com o
   * protótipo, hoje com uma opção só. O campo de busca livre "CNPJ ou Razão Social" do
   * protótipo fica de fora: com um único CNPJ ele seria redundante com este select. */
  const cnpjOpcoes = useMemo(
    () => [
      { value: "", label: "Todos" },
      ...(cnpj ? [{ value: cnpj, label: formatarCnpj(cnpj) }] : []),
    ],
    [cnpj]
  );

  const competenciasDisponiveis = useMemo(() => {
    const set = new Set(linhas.map((linha) => competenciaYm(linha.competencia)));
    return Array.from(set).sort();
  }, [linhas]);

  const competenciaOpcoes = useMemo(
    () => [
      { value: "", label: "Todas" },
      ...competenciasDisponiveis.map((ym) => ({ value: ym, label: fmtMesAno(ym) })),
    ],
    [competenciasDisponiveis]
  );

  const linhasFiltradas = useMemo(() => {
    let base = linhas;
    if (cnpjFiltro && cnpjFiltro !== cnpj) {
      base = [];
    }
    // Busca livre do protótipo: casa contra o CNPJ (só dígitos) ou a razão social da
    // apuração. Com um CNPJ só, ou casa e mantém tudo, ou não casa e zera.
    const termo = buscaFiltro.trim().toLowerCase();
    if (termo) {
      const digitos = termo.replace(/\D/g, "");
      const casaCnpj = digitos.length > 0 && cnpj.includes(digitos);
      const casaEmpresa = empresa.toLowerCase().includes(termo);
      if (!casaCnpj && !casaEmpresa) base = [];
    }
    if (competenciaFiltro) {
      base = base.filter((linha) => competenciaYm(linha.competencia) === competenciaFiltro);
    }
    if (faltaE111Filtro) {
      const querFalta = faltaE111Filtro === "sim";
      base = base.filter((linha) => calcularFaltaE111(linha) === querFalta);
    }
    return [...base].sort((a, b) =>
      competenciaYm(a.competencia).localeCompare(competenciaYm(b.competencia))
    );
  }, [linhas, cnpj, empresa, cnpjFiltro, buscaFiltro, competenciaFiltro, faltaE111Filtro]);

  const totais = useMemo(() => calcularTotais(linhasFiltradas), [linhasFiltradas]);

  useEffect(() => {
    onTotaisChange?.(totais);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [totais]);

  // Enfileira e acompanha o job até o download (antes só enfileirava e o arquivo nunca
  // chegava ao usuário) — ver Detalhe/hooks/useExportacaoAssincrona.js.
  const exportacao = useExportacaoAssincrona({
    apuracaoId,
    chave: "lancamentos",
    enfileirar: () =>
      exportarSumarizacaoApuracao(apuracaoId, { tipo: "lancamentos", formato: "csv" }),
    onFinalizar: ({ severity, msg }) => setInfoSnack({ open: true, msg, severity }),
  });

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 6 }}>
        <GridLoader color="#1565c0" size={12} />
      </Box>
    );
  }

  if (erro) {
    const emAndamento = sumarizacaoEmAndamento(erro);
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity={emAndamento ? "info" : "error"}>
          {emAndamento
            ? "Sumarização em processamento — os dados aparecerão aqui assim que for concluída."
            : erro}
        </Alert>
      </Box>
    );
  }

  return (
    <>
      <ApuracaoCardShell
        bare={bare}
        conteudoComScrollProprio
        Icone={AssessmentIcon}
        titulo="Resumo por período (registro E110 — SPED Fiscal)"
        tituloSx={{ fontSize: "1.25rem" }}
        acaoHeader={
          <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 1.5 }}>
            <Typography sx={{ color: "#666", fontSize: "0.8rem", whiteSpace: "nowrap" }}>
              {linhasFiltradas.length} registros no filtro
            </Typography>
            {/* TXM-472 — "Classificação NCM" saiu daqui: o card manda que fique no cabeçalho
                geral da tela (topo direito, mesma linha do título), visível em qualquer aba.
                Ver DetalheIcms/index.js. */}
            <BotaoExportacaoProgresso
              progresso={exportacao}
              rotulo="Exportar CSV"
              variant="contained"
              size="small"
              disabled={!podeExportar}
              onClick={() => exportacao.iniciar()}
              sx={{ borderRadius: "8px", textTransform: "none", fontWeight: 600, bgcolor: "#18316e", minHeight: 40, whiteSpace: "nowrap", px: 2, "&:hover": { bgcolor: "#152858" } }}
            />
            {/* TXM-472 — reprocessamento por aba dependia do motor de apuração (TXM-601);
                ambos concluídos, botão habilitado, mesmo padrão do Reprocessar do CAT-42. */}
            <Button
              variant="outlined"
              size="small"
              onClick={() => setModalReprocessarAberto(true)}
              sx={{ borderRadius: "8px", textTransform: "none", fontWeight: 600, whiteSpace: "nowrap", minHeight: 40, color: "#18316e", borderColor: "#18316e", "&:hover": { borderColor: "#152858", bgcolor: "rgba(24, 49, 110, 0.04)" } }}
            >
              Reprocessar
            </Button>
          </Box>
        }
        filtros={
          <>
            <SelectFiltro label="CNPJ" value={cnpjFiltro} onChange={setCnpjFiltro} options={cnpjOpcoes} />
            <BuscaFiltro
              label="CNPJ ou Razão Social"
              placeholder="Pesquisar CNPJ ou razão social"
              value={buscaFiltro}
              onChange={setBuscaFiltro}
              width={280}
            />
            <SelectFiltro label="Competência" value={competenciaFiltro} onChange={setCompetenciaFiltro} options={competenciaOpcoes} />
            <SelectFiltro label="Falta E111" value={faltaE111Filtro} onChange={setFaltaE111Filtro} options={FALTA_E111_OPCOES} />
            <Button
              variant="outlined"
              size="small"
              onClick={() => {
                setCnpjFiltro("");
                setBuscaFiltro("");
                setCompetenciaFiltro("");
                setFaltaE111Filtro("");
              }}
              sx={{ borderRadius: "8px", textTransform: "none", fontWeight: 600, whiteSpace: "nowrap", minHeight: 40, color: "#18316e", borderColor: "#18316e", "&:hover": { borderColor: "#152858", bgcolor: "rgba(24, 49, 110, 0.04)" } }}
            >
              Limpar filtros
            </Button>
          </>
        }
        paginacao={
          linhasFiltradas.length > 0 && (
            <Pagination
              paginationKey="aba-icms"
              data={linhasFiltradas}
              filteredData={linhasPagina}
              setFilteredData={setLinhasPagina}
            />
          )
        }
      >
        {linhasFiltradas.length === 0 ? (
          <p className={styles.emptyMsg}>Nenhuma competência encontrada para esta apuração.</p>
        ) : (
          <div style={{ margin: "8px 0 0", overflow: "auto" }}>
          <table className={tableStyles.accordion_table}>
            <thead>
              <tr>
                <th scope="col" className={`${tableStyles.stickyKeyHeader} ${tableStyles.stickyKeyCol1}`}>CNPJ</th>
                <th scope="col" className={`${tableStyles.stickyKeyHeader} ${tableStyles.stickyKeyCol2}`}>Competência</th>
                <th scope="col" style={NUM_TH}>Tot. débitos</th>
                <th scope="col" style={NUM_TH}>Aj. débitos</th>
                <th scope="col" style={NUM_TH}>Tot. aj. débitos</th>
                <th scope="col" style={NUM_TH}>Estornos créd.</th>
                <th scope="col" style={NUM_TH}>Tot. créditos</th>
                <th scope="col" style={NUM_TH}>Aj. créditos</th>
                <th scope="col" style={NUM_TH}>Tot. aj. créditos</th>
                <th scope="col" style={NUM_TH}>Estornos déb.</th>
                <th scope="col" style={NUM_TH}>Sld credor ant.</th>
                <th scope="col" style={NUM_TH}>Sld apurado</th>
                <th scope="col" style={NUM_TH}>Tot. deduções</th>
                <th scope="col" style={NUM_TH}>ICMS a recolher</th>
                <th scope="col" style={NUM_TH}>Sld credor a transportar</th>
                <th scope="col" style={NUM_TH}>Déb. especiais</th>
                <th scope="col">Falta E111</th>
                {/* "Ações"/"Lançamentos" (LancamentosIcmsE111) não existe no protótipo estático
                    apuracao_icms_detalhes.html — foi adicionado depois como drill-down real dos
                    ajustes E111 por competência, já que "Falta E111" sozinho não mostra o
                    detalhe. Funcionalidade nova e intencional, não uma divergência a corrigir. */}
                <th scope="col">Ações</th>
              </tr>
            </thead>
            <tbody>
              {linhasPagina.map((linha) => {
                const faltaE111 = calcularFaltaE111(linha);
                return (
                  <tr key={linha.competencia}>
                    <td className={`${tableStyles.stickyKeyCell} ${tableStyles.stickyKeyCol1}`}>{formatarCnpj(cnpj)}</td>
                    <td className={`${tableStyles.stickyKeyCell} ${tableStyles.stickyKeyCol2}`}>{fmtMesAno(competenciaYm(linha.competencia))}</td>
                    <td style={NUM_TD}>{formatarMoedaBRL(linha.vlTotDebitos)}</td>
                    <td style={NUM_TD}>{formatarMoedaBRL(linha.vlAjDebitos)}</td>
                    <td style={NUM_TD}>{formatarMoedaBRL(linha.vlTotAjDebitos)}</td>
                    <td style={NUM_TD}>{formatarMoedaBRL(linha.vlEstornosCred)}</td>
                    <td style={NUM_TD}>{formatarMoedaBRL(linha.vlTotCreditos)}</td>
                    <td style={NUM_TD}>{formatarMoedaBRL(linha.vlAjCreditos)}</td>
                    <td style={NUM_TD}>{formatarMoedaBRL(linha.vlTotAjCreditos)}</td>
                    <td style={NUM_TD}>{formatarMoedaBRL(linha.vlEstornosDeb)}</td>
                    <td style={NUM_TD}>{formatarMoedaBRL(linha.vlSldCredorAnt)}</td>
                    <td style={NUM_TD}>{formatarMoedaBRL(linha.vlSldApurado)}</td>
                    <td style={NUM_TD}>{formatarMoedaBRL(linha.vlTotDed)}</td>
                    <td style={NUM_TD}>{formatarMoedaBRL(linha.vlIcmsRecolher)}</td>
                    <td style={NUM_TD}>{formatarMoedaBRL(linha.vlSldCredorTransportar)}</td>
                    <td style={NUM_TD}>{formatarMoedaBRL(linha.debEsp)}</td>
                    <td>
                      <span
                        className={styles.badgeTipo}
                        style={faltaE111 ? { background: "#fdecea", color: "#b71c1c" } : undefined}
                      >
                        {faltaE111 ? "Sim" : "Não"}
                      </span>
                    </td>
                    <td>
                      <button
                        type="button"
                        className={`${styles.btnHeader} ${styles.btnHeaderPrimary}`}
                        onClick={() =>
                          navigate(`/relatorios/apuracoes/${apuracaoId}/detalhe/icms/lancamentos?competencia=${competenciaYm(linha.competencia)}`)
                        }
                      >
                        Lançamentos
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          </div>
        )}
      </ApuracaoCardShell>

      <Snackbar
        open={infoSnack.open}
        autoHideDuration={4000}
        onClose={() => setInfoSnack((s) => ({ ...s, open: false }))}
      >
        <Alert severity={infoSnack.severity}>{infoSnack.msg}</Alert>
      </Snackbar>

      <ModalReprocessarAba
        open={modalReprocessarAberto}
        onClose={() => setModalReprocessarAberto(false)}
        onSuccess={(msg) => setInfoSnack({ open: true, msg, severity: "success" })}
        onError={(msg) => setInfoSnack({ open: true, msg, severity: "error" })}
        titulo="Reprocessar apuração ICMS"
        descricao="Recalcula o resumo por período (registro E110) com os dados fiscais atuais."
        cnpj={cnpj}
        empresa={empresa}
        idApuracao={apuracaoId}
      />
    </>
  );
}
